import {
    pickValuesStartWith,
    validateCpr,
    validateAccountNumber,
    validateBankNumber,
    //validateCprAge,
    validateEmail,
    validatePhone
} from 'flos-react-cvi';
import { createSelector } from 'reselect';
import { InsuranceType, IStore, ModelIndexType } from '../type';

const selectUiState = (state: IStore) => state.ui;

export const selectResources = (state: IStore) => selectUiState(state).resources;
export const selectUiStatus = (state: IStore) => selectUiState(state).status;
export const selectEndpoints = (state: IStore) => selectUiState(state).endpoints;
export const selectPrimaryType = (state: IStore) => selectUiState(state).primaryType;
export const selectIsPrimaryType = (state: IStore, insuranceType: InsuranceType) =>
    selectPrimaryType(state) === insuranceType;
export const selectCurrentStep = (state: IStore) => selectUiState(state).currentStep;
export const selectUpdatingCoverage = (state: IStore) => selectUiState(state).updatingCoverage;
export const selectDataPolicyContentBlock = (state: IStore) => selectUiState(state).dataPolicyContentBlock;
export const selectTerminationFeeContentBlock = (state: IStore) => selectUiState(state).terminationFeeContentBlock;

const getModelParamValue = (state: IStore, param: ModelIndexType) => {
    const model = selectModel(state);
    return model && model[param];
};

export const selectModel = (state: IStore) => selectFormState(state).model;
export const selectOfferNumber = (state: IStore) => getModelParamValue(state, 'offerNumber');
export const selectDisabilityCoverageAmount = (state: IStore) => getModelParamValue(state, 'disabilityCoverageAmount');
export const selectObjectId = (state: IStore) => {
    const model = selectModel(state);
    const primaryType = selectPrimaryType(state);
    return (
        model && model.objectIds && (primaryType === 'accident' ? model.objectIds.accident : model.objectIds.contents)
    );
};
export const selectAccidentObjectId = (state: IStore) => {
    const model = selectModel(state);
    return model && model.objectIds && model.objectIds.accident;
};
export const selectCoverages = (state: IStore) => {
    const model = selectModel(state);
    return model && model.coverages;
};
export const selectHasSelectedInsuranceType = (state: IStore, insuranceType: InsuranceType) => {
    const model = selectModel(state);
    return (
        !!model && model.coverages[insuranceType] && model.coverages[insuranceType].some(coverage => coverage.chosen)
    );
};

export const selectPermanentInjuryAmountOptions = createSelector(selectResources, resources =>
    pickValuesStartWith(resources, 'coveragePermanentInjuryAmount\\.option\\.')
);

export const selectDisabilityCoverageAmountDisplay = createSelector(
    selectPermanentInjuryAmountOptions,
    selectDisabilityCoverageAmount,
    (injuryAmountOptions, disabilityCoverageAmount) => {
        return disabilityCoverageAmount
            ? injuryAmountOptions
                  .filter(({ key }) => key === String(disabilityCoverageAmount))
                  .map(({ value }) => value)[0]
            : '';
    }
);

const selectFormState = (state: IStore) => state.form;

export const selectSelectedCoverages = (state: IStore) => selectFormState(state).coverageSettings.selectedCoverages;

export const selectCurrentInsuranceDetails = (state: IStore, insuranceType: InsuranceType) =>
    selectFormState(state).currentInsurance[insuranceType];

export const selectNewInsuranceStartDate = (state: IStore) =>
    selectFormState(state).currentInsurance.newInsuranceStartDate;

export const selectFormMetadata = (state: IStore) => selectFormState(state).meta;

export const selectCurrentInsurance = (state: IStore) => selectFormState(state).currentInsurance;

export const selectShouldShowNewDateInput = (state: IStore, insuranceType: InsuranceType) =>
    selectCurrentInsurance(state).showNewInsuranceStartDateInputType === insuranceType;

export const selectCurrentInsuranceTypeIsChecked = (state: IStore, insuranceType: InsuranceType) =>
    selectCurrentInsuranceDetails(state, insuranceType).shouldTerminate !== null;

export const selectHasContentInsurance = (state: IStore) => selectUiState(state).hasContentInsurance;

export const selectCurrentInsuranceTypeIsValid = (state: IStore, insuranceType: InsuranceType) => {
    if (!selectHasSelectedInsuranceType(state, insuranceType)) return true;
    const { shouldTerminate, currentInsuranceCompanyId } = selectCurrentInsuranceDetails(state, insuranceType);
    return shouldTerminate !== null && (!shouldTerminate || !!currentInsuranceCompanyId);
};

export const selectCustomerName = (state: IStore) => selectFormState(state).customer.name;
export const selectCustomerCpr = (state: IStore) => selectFormState(state).customer.cprNumber;
export const selectCustomerAddress = (state: IStore) => selectFormState(state).customer.address;
export const selectCustomerMobile = (state: IStore) => selectFormState(state).customer.mobile;
export const selectCustomerEmail = (state: IStore) => selectFormState(state).customer.email;
export const selectSkybrudIsChecked = (state: IStore) => selectFormState(state).customer.skybrudIsChecked;
export const selectSkaderIsChecked = (state: IStore) => selectFormState(state).customer.skaderIsChecked;
export const selectGaeldIsChecked = (state: IStore) => selectFormState(state).customer.gaeldIsChecked;
export const selectCustomerInfoValues = (state: IStore) => selectFormState(state).customer;

export const selectCustomerNameIsValid = (state: IStore) => {
    const name = selectCustomerName(state);

    return !!name && name.split(' ').length > 1;
};

interface ValidateCprAge {
    minAge: number;
    maxAge: number;
}
const validateCprAge = (cprNumber: string, { minAge, maxAge }: ValidateCprAge): string | null => {
    const parts = cprNumber.substring(0, 6).match(/..?/g);
    if (parts && parts.length !== 3) {
        return 'INVALID_FORMAT';
    }

    const tempThisYear = new Date().getFullYear();
    const cprBirthYear = parts ? (+parts[2] > tempThisYear % 1000 ? +parts[2] + 1900 : +parts[2] + 2000) : 0;
    // if more than 26 years old then add 39 days to ensure, that customer has 40 until 28th birthday
    const daysBeforeBirthDate = tempThisYear - cprBirthYear > 20 ? 39 : 0;
    const date = new Date();
    date.setDate(date.getDate() + daysBeforeBirthDate);

    const thisYear = date.getFullYear();
    const thisMonth = date.getMonth() + 1;
    const thisDate = date.getDate();

    const cprDate = parts ? +parts[0] : 0;
    const cprMonth = parts ? +parts[1] : 0;
    const cprYear = parts ? (+parts[2] > thisYear % 1000 ? +parts[2] + 1900 : +parts[2] + 2000) : 0;

    let age = thisYear - cprYear;
    if (thisMonth < cprMonth || (thisMonth === cprMonth && thisDate < cprDate)) {
        age--;
    }

    date.setDate(date.getDate() - daysBeforeBirthDate);

    if (age < minAge) {
        return 'TOO_YOUNG';
    } else if (age > maxAge) {
        return 'TOO_OLD';
    }
    return null;
};

export const selectCustomerCprErrorType = (state: IStore) => {
    const cpr = selectCustomerCpr(state);

    return validateCpr(cpr)
        ? validateCprAge(cpr, {
              minAge: 18,
              maxAge: 27
          })
        : 'INVALID_FORMAT';
};

export const selectCustomerCprErrorMessage = (state: IStore) => {
    /**
     * Put the error message mapping in selector, not sure if it's a good idea,
     * but I doesn't like putting this logic in React component either
     */
    const error = selectCustomerCprErrorType(state);
    const resources = selectResources(state);

    switch (error) {
        // we're taking extra precaution to provide a default error message because
        // we want the input still marked as error even it's not included in resource bundle
        case 'TOO_OLD':
        case 'TOO_YOUNG':
            return (
                resources.customerInfoSectionCprAgeRangeError ||
                'Du skal være mellem 18 og 28 år for at købe en rejseforsikring til unge'
            );

        case 'INVALID_FORMAT':
            return (
                resources.customerInfoSectionCprFormatError || 'Det indtastede nummer er ikke et gyldigt CPR-nummer.'
            );

        default:
            return '';
    }
};

export const selectCustomerAdddressIsValid = (state: IStore) => !!selectCustomerAddress(state);

export const selectCustomerMobileIsValid = (state: IStore) => validatePhone(selectCustomerMobile(state));

export const selectCustomerEmailIsValid = (state: IStore) => validateEmail(selectCustomerEmail(state));

export const selectCustomerInfoIsValid = (state: IStore) =>
    selectCustomerNameIsValid(state) &&
    selectCustomerCprErrorType(state) === null &&
    selectCustomerAdddressIsValid(state) &&
    selectCustomerMobileIsValid(state) &&
    selectCustomerEmailIsValid(state) &&
    (!selectHasSelectedInsuranceType(state, 'contents') ||
        (selectSkaderIsChecked(state) && selectSkybrudIsChecked(state) && selectGaeldIsChecked(state)));

export const selectPaymentInfoValues = (state: IStore) => selectFormState(state).payment;

export const selectRegNumIsValid = (state: IStore) => {
    const { isPaymentValid, regNum } = selectPaymentInfoValues(state);
    return isPaymentValid && validateBankNumber(regNum);
};

export const selectAccountNumIsValid = (state: IStore) => {
    const { isPaymentValid, accountNum } = selectPaymentInfoValues(state);
    return isPaymentValid && validateAccountNumber(accountNum, 7);
};

export const selectAccountOwnerCprIsValid = (state: IStore) =>
    validateCpr(selectPaymentInfoValues(state).accountHolderCprNumber);

export const selectPaymentInfoIsValid = (state: IStore) => {
    const values = selectPaymentInfoValues(state);
    return (
        selectRegNumIsValid(state) &&
        selectAccountNumIsValid(state) &&
        (!values.isSomeoneElseAccount || selectAccountOwnerCprIsValid(state)) &&
        values.agreeObtainInfo &&
        values.agreeTermsOfPurchase
    );
};

export const selectProductName = (state: IStore, insuranceType: InsuranceType) => {
    const model = selectModel(state);
    return model && model.productNames[insuranceType];
};
